import React, { useState, useEffect, useCallback } from 'react';
import './CorporateLifeHacks.css';

const DEFAULT_BATCH = 20;
const LOAD_MORE_THRESHOLD = 300;

function CorporateLifeHacks({ theme, onBackClick }) {
    const [entries, setEntries] = useState([]);
    const [visibleCount, setVisibleCount] = useState(DEFAULT_BATCH);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadEntries = async () => {
            try {
                const response = await fetch('/corporate-life-hacks.json');
                if (!response.ok) {
                    throw new Error(`Unable to load content: ${response.status}`);
                }
                const data = await response.json();
                if (!cancelled) {
                    setEntries(Array.isArray(data) ? data : []);
                    setVisibleCount(DEFAULT_BATCH);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error(err);
                    setError('Could not load content. Please try again later.');
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadEntries();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleScroll = useCallback(() => {
        if (isLoading || entries.length <= visibleCount) return;
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight - LOAD_MORE_THRESHOLD) {
            setVisibleCount((prev) => Math.min(entries.length, prev + DEFAULT_BATCH));
        }
    }, [entries.length, isLoading, visibleCount]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const visibleEntries = entries.slice(0, visibleCount);

    return (
        <div className={`app ${theme}`}>
            <main>
                <section className="lifehacks-header-section">
                    <div className="lifehacks-header">
                        <div>
                            <h1>Corporate Life Hacks</h1>
                            <p className="lifehacks-subtitle">
                                Survival skills for the corporate ecosystem - Knowledge shared by seasoned senior employees. Results not guaranteed.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Back to Home
                        </button>
                    </div>
                </section>

                <section className="lifehacks-content">
                    {isLoading ? (
                        <div className="lifehacks-status">Loading life hacks…</div>
                    ) : error ? (
                        <div className="lifehacks-status lifehacks-error">{error}</div>
                    ) : (
                        <>
                            <div className="lifehacks-list">
                                {visibleEntries.map((entry, index) => (
                                    entry.type === 'section' ? (
                                        <div key={`section-${index}`} className="lifehacks-section-heading">
                                            {entry.text}
                                        </div>
                                    ) : (
                                        <article key={`entry-${index}`} className="lifehacks-card">
                                            <span className="lifehacks-index">{index + 1}.</span>
                                            <p>{entry.text}</p>
                                        </article>
                                    )
                                ))}
                            </div>

                            <div className="lifehacks-footer-note">
                                {visibleCount < entries.length
                                    ? 'Scroll to load more life hacks...'
                                    : 'You have reached the end of the corporate survival guide.'}
                            </div>
                        </>
                    )}
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default CorporateLifeHacks;
